import { useState, useEffect, useRef } from 'react';
import { collection, onSnapshot, doc, deleteDoc, updateDoc, query, orderBy, addDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Trash2, Mail, Loader2, AlertCircle, Plus, X, UploadCloud, MessageCircle, Users } from 'lucide-react';

export default function AdminCustomers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const fileInputRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isPushing, setIsPushing] = useState(false);
  const navigate = useNavigate();

  // Toggle Single User
  const toggleUser = (id) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]);
  };

  // Toggle All Users
  const toggleAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  // Handle Delete Selected
  const handleDeleteSelected = async () => {
    if (selectedUsers.length === 0) return;
    if (!window.confirm(`هل أنت متأكد من رغبتك في حذف ${selectedUsers.length} عميل تحديداً؟ لا يمكن التراجع.`)) return;

    setIsDeleting('bulk');
    try {
      // Firestore batches support max 500 operations
      const chunks = [];
      for (let i = 0; i < selectedUsers.length; i += 500) {
        chunks.push(selectedUsers.slice(i, i + 500));
      }

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => {
          batch.delete(doc(db, 'customers', id));
        });
        await batch.commit();
      }

      setSelectedUsers([]);
    } catch (error) {
      console.error("Error bulk deleting:", error);
      alert('حدث خطأ أثناء الحذف.');
    } finally {
      setIsDeleting(null);
    }
  };

  // Push Selected To Registrations
  const handlePushToRegistrations = async () => {
    if (selectedUsers.length === 0) return;
    if (!window.confirm(`هل أنت متأكد من نسخ ${selectedUsers.length} عميل إلى قائمة المسجلين؟`)) return;

    setIsPushing(true);
    try {
      const selectedData = users.filter(u => selectedUsers.includes(u.id));
      const batch = writeBatch(db);

      selectedData.forEach(u => {
        const newRef = doc(collection(db, 'registrations'));
        batch.set(newRef, {
          name: u.name,
          whatsapp: u.whatsapp,
          email: u.email || '',
          level: u.level || 'beginner',
          status: 'interested',
          amountPaid: 0,
          notes: u.notes || 'منقول من قاعدة العملاء',
          source: 'transfer',
          createdAt: serverTimestamp()
        });
      });
      await batch.commit();

      setSelectedUsers([]);
      alert('✅ تم نسخهم بنجاح إلى فريق المسجلين!');
    } catch (err) {
      console.error("Error pushing to registrations:", err);
      alert('حدث خطأ أثناء النسخ.');
    } finally {
      setIsPushing(false);
    }
  };

  // Send to WhatsApp Automation
  const handleSendToWhatsApp = () => {
    if (selectedUsers.length === 0) return;
    sessionStorage.setItem('lobab_wa_targets', JSON.stringify(selectedUsers));
    navigate('/admin/whatsapp');
  };

  // Filters State
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterDate, setFilterDate] = useState('');

  const [filterSource, setFilterSource] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Notes State
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [currentNoteUser, setCurrentNoteUser] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Add Client State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    whatsapp: '',
    email: '',
    level: 'beginner',
    amountPaid: 0,
  });

  useEffect(() => {
    const q = query(collection(db, 'customers'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dateStr: doc.data().createdAt
          ? doc.data().createdAt.toDate().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
          : 'غير معروف'
      }));
      setUsers(usersData);
      setFilteredUsers(usersData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching users:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Search and Filter Logic
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = users.filter(user => {
      const noteMatch = user.notes && user.notes.toLowerCase().includes(term);
      const matchesSearch = (user.name && user.name.toLowerCase().includes(term)) ||
        (user.whatsapp && user.whatsapp.includes(term)) ||
        noteMatch;

      const currentStatus = user.status || 'interested';
      const matchesStatus = filterStatus === 'all' || currentStatus === filterStatus;

      const currentLevel = user.level || 'beginner';
      const matchesLevel = filterLevel === 'all' || currentLevel === filterLevel;

      let matchesDate = true;
      if (filterDate) {
        if (user.createdAt && user.createdAt.toDate) {
          const userDate = user.createdAt.toDate();
          const filterParts = filterDate.split('-');
          const fy = parseInt(filterParts[0], 10);
          const fm = parseInt(filterParts[1], 10) - 1;
          const fd = parseInt(filterParts[2], 10);

          if (userDate.getFullYear() !== fy || userDate.getMonth() !== fm || userDate.getDate() !== fd) {
            matchesDate = false;
          }
        } else {
          matchesDate = false;
        }
      }

      const matchesSource = filterSource === 'all' || user.source === filterSource || (!user.source && filterSource === 'manual');

      return matchesSearch && matchesStatus && matchesLevel && matchesDate && matchesSource;
    });

    // Sorting Logic
    if (sortBy === 'lastMessage') {
      filtered.sort((a, b) => {
        const dateA = a.lastChatDate || 0;
        const dateB = b.lastChatDate || 0;
        return dateB - dateA; // Descending by last chat
      });
    } else {
      // newest (descending by createdAt)
      filtered.sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || new Date(0).getTime();
        const dateB = b.createdAt?.toMillis?.() || new Date(0).getTime();
        return dateB - dateA;
      });
    }

    setFilteredUsers(filtered);
  }, [searchTerm, filterStatus, filterLevel, filterDate, filterSource, sortBy, users]);

  // Handle Status Change
  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, 'customers', id), { status: newStatus });
      setUsers(prev => prev.map(user => user.id === id ? { ...user, status: newStatus } : user));
      setFilteredUsers(prev => prev.map(user => user.id === id ? { ...user, status: newStatus } : user));
    } catch (error) {
      console.error("Error updating status:", error);
      alert('حدث خطأ أثناء تعديل التصنيف.');
    }
  };

  // Handle Amount Change
  const handleAmountChange = async (id, amount) => {
    const numAmount = parseFloat(amount) || 0;
    try {
      await updateDoc(doc(db, 'customers', id), { amountPaid: numAmount });
      setUsers(prev => prev.map(user => user.id === id ? { ...user, amountPaid: numAmount } : user));
      setFilteredUsers(prev => prev.map(user => user.id === id ? { ...user, amountPaid: numAmount } : user));
    } catch (error) {
      console.error("Error updating amount:", error);
      alert('حدث خطأ أثناء حفظ المبلغ.');
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في حذف هذا المسجل نهائياً؟ لا يمكن التراجع عن هذا الإجراء.')) return;

    setIsDeleting(id);
    try {
      await deleteDoc(doc(db, 'customers', id));
      // Remove from UI without refetching
      setUsers(prev => prev.filter(user => user.id !== id));
    } catch (error) {
      console.error("Error deleting document:", error);
      alert('حدث خطأ أثناء الحذف.');
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle Add Client
  const handleAddClient = async (e) => {
    e.preventDefault();
    if (!newClient.name || !newClient.whatsapp) {
      alert('الرجاء إدخال الاسم ورقم الواتساب');
      return;
    }

    setIsAdding(true);
    try {
      await addDoc(collection(db, 'customers'), {
        name: newClient.name,
        whatsapp: newClient.whatsapp,
        email: newClient.email || '',
        level: newClient.level,
        status: 'customer',
        amountPaid: parseFloat(newClient.amountPaid) || 0,
        source: 'manual',
        createdAt: serverTimestamp()
      });

      setIsAddModalOpen(false);
      setNewClient({
        name: '',
        whatsapp: '',
        email: '',
        level: 'beginner',
        amountPaid: 0,
      });

      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error("Error adding client:", error);
      alert('حدث خطأ أثناء إضافة العميل.');
    } finally {
      setIsAdding(false);
    }
  };

  // Handle Save Note
  const handleSaveNote = async () => {
    if (!currentNoteUser) return;
    setIsSavingNote(true);
    try {
      await updateDoc(doc(db, 'customers', currentNoteUser.id), { notes: noteText });
      setUsers(prev => prev.map(user => user.id === currentNoteUser.id ? { ...user, notes: noteText } : user));
      setFilteredUsers(prev => prev.map(user => user.id === currentNoteUser.id ? { ...user, notes: noteText } : user));
      setIsNotesModalOpen(false);
    } catch (error) {
      console.error("Error updating notes:", error);
      alert('حدث خطأ أثناء حفظ الملاحظات.');
    } finally {
      setIsSavingNote(false);
      setCurrentNoteUser(null);
    }
  };

  // Export to CSV Logic
  const handleExportCSV = () => {
    if (users.length === 0) return;

    // CSV Headers
    const headers = ['الاسم', 'البريد الإلكتروني', 'رقم الواتساب', 'المستوى', 'التصنيف', 'المبلغ ($)', 'الملاحظات', 'المصدر', 'تاريخ التسجيل', 'آخر محادثة'];

    // Map Data
    const csvRows = users.map(user => {
      const levelAr = user.level === 'beginner' ? 'مبتدئ' : user.level === 'intermediate' ? 'متوسط' : user.level === 'advanced' ? 'متقدم' : user.level;
      const statusAr = user.status === 'customer' ? 'عميل' : user.status === 'contacted' ? 'تم التواصل معه' : 'مهتم';
      const amount = user.status === 'customer' ? (user.amountPaid || 0) : 0;
      const notesCleaned = user.notes ? user.notes.replace(/"/g, '""') : '';
      const sourceMap = user.source === 'whatsapp-scraper' ? 'واتساب' : user.source === 'csv' ? 'استيراد CSV' : 'إضافة يدوية';

      // Wrap values in quotes to handle commas and RTL characters cleanly
      const lastChatFormat = user.lastChatDate ? new Date(user.lastChatDate).toLocaleDateString('ar-EG') : '';
      return `"${user.name}","${user.email || ''}","\u200E${user.whatsapp}","${levelAr}","${statusAr}","${amount}","${notesCleaned}","${sourceMap}","${user.dateStr}","${lastChatFormat}"`;
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Add BOM for UTF-8 Excel support
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `عملاء_لباب_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // CSV Import Logic
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const rows = text.split('\n').filter(row => row.trim().length > 0);
        if (rows.length < 2) {
          alert('الملف فارغ أو لا يحتوي على بيانات صحيحة.');
          setIsImporting(false);
          return;
        }

        const parseRow = (str) => {
          const arr = []; let quote = false, col = '';
          for (let i = 0; i < str.length; i++) {
            let cc = str[i];
            if (cc === '"' && quote && str[i + 1] === '"') { col += '"'; i++; continue; }
            if (cc === '"') { quote = !quote; continue; }
            if (cc === ',' && !quote) { arr.push(col); col = ''; continue; }
            col += cc;
          }
          arr.push(col);
          return arr.map(c => c.trim().replace(/^["\u200E\uFEFF]+|["\u200E\uFEFF]+$/g, ''));
        };

        const headers = parseRow(rows[0]).map(h => h.toLowerCase());
        let nameIndex = headers.findIndex(h => h.includes('اسم') || h.includes('name'));
        let phoneIndex = headers.findIndex(h => h.includes('رقم') || h.includes('واتساب') || h.includes('phone') || h.includes('number'));

        const hasHeaders = nameIndex !== -1 && phoneIndex !== -1;
        if (!hasHeaders) {
          nameIndex = 0;
          phoneIndex = headers.length > 1 ? 1 : 0;
        }

        const batchPromises = [];
        let importedCount = 0;
        const startIndex = hasHeaders ? 1 : 0;

        for (let i = startIndex; i < rows.length; i++) {
          const cleanCols = parseRow(rows[i]);

          let name = cleanCols[nameIndex] || 'عميل مستورد';
          let phone = (cleanCols[phoneIndex] || '').replace(/\D/g, '');

          if (phone.length >= 8) {
            batchPromises.push(
              addDoc(collection(db, 'customers'), {
                name: name,
                whatsapp: phone,
                email: '',
                level: 'beginner',
                status: 'interested',
                amountPaid: 0,
                source: 'csv',
                notes: 'تم استيراده من ملف CSV',
                createdAt: serverTimestamp()
              })
            );
            importedCount++;
          }
        }

        if (batchPromises.length > 0) {
          await Promise.all(batchPromises);
          alert(`🎉 تم استيراد ${importedCount} جهة اتصال بنجاح إلى قاعدة البيانات.`);
        } else {
          alert('لم يتم العثور على أرقام هواتف صالحة في الملف!');
        }

      } catch (error) {
        console.error('Error importing CSV:', error);
        alert('حدث خطأ أثناء محاولة قراءة الملف.');
      } finally {
        setIsImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">قاعدة العملاء</h1>
          <p className="text-slate-500 dark:text-zinc-400">إدارة قاعدة العملاء الكلية، وأولئك المستخرجين عن طريق تحليل الذكاء الاصطناعي و أتمتة الواتساب.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <input type="file" accept=".csv" ref={fileInputRef} onChange={handleImportCSV} className="hidden" />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="flex items-center justify-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:hover:bg-blue-500/20 px-5 py-2.5 rounded-xl font-bold transition-all disabled:opacity-50 shadow-sm border border-blue-200 dark:border-blue-500/30"
          >
            {isImporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
            <span>استيراد CSV</span>
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-sm"
          >
            <Plus className="w-5 h-5" />
            <span>إضافة عميل</span>
          </button>

          <button
            onClick={handleExportCSV}
            disabled={users.length === 0}
            className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white px-5 py-2.5 rounded-xl font-bold transition-all disabled:opacity-50 shadow-sm"
          >
            <Download className="w-5 h-5" />
            <span>تصدير CSV</span>
          </button>

          {selectedUsers.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleSendToWhatsApp}
                className="flex items-center justify-center gap-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20 px-4 py-2 rounded-xl text-mdfont-bold transition-all shadow-sm border border-emerald-200 dark:border-emerald-500/30"
              >
                <MessageCircle className="w-5 h-5" />
                <span>مراسلة المحددين واتساب</span>
              </button>

              <button
                onClick={handlePushToRegistrations}
                disabled={isPushing}
                className="flex items-center justify-center gap-2 bg-purple-50 text-purple-600 hover:bg-purple-100 dark:bg-purple-500/10 dark:text-purple-400 dark:hover:bg-purple-500/20 px-4 py-2 rounded-xl text-mdfont-bold transition-all shadow-sm border border-purple-200 dark:border-purple-500/30 disabled:opacity-50"
              >
                {isPushing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Users className="w-5 h-5" />}
                <span>نقل للمسجلين</span>
              </button>

              <button
                onClick={handleDeleteSelected}
                disabled={isDeleting === 'bulk'}
                className="flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 px-4 py-2 rounded-xl text-mdfont-bold transition-all shadow-sm border border-red-200 dark:border-red-500/30 disabled:opacity-50"
              >
                {isDeleting === 'bulk' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                <span>حذف المحدد ({selectedUsers.length})</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm">

        {/* Toolbar & Filters */}
        <div className="p-4 md:p-6 border-b border-slate-200 dark:border-white/10 flex flex-col gap-4 bg-slate-50/50 dark:bg-zinc-900/50">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:max-w-md">
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="ابحث بالاسم أو رقم الواتساب..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-11 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-slate-900 dark:text-white"
              />
            </div>
            <div className="text-lg font-bold text-slate-500 dark:text-zinc-400 whitespace-nowrap">
              إجمالي النتائج: <span className="text-primary-600 dark:text-primary-400">{filteredUsers.length}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            >
              <option value="all">كل التصنيفات</option>
              <option value="interested">مهتم</option>
              <option value="contacted">تم التواصل معه</option>
              <option value="customer">عميل</option>
            </select>

            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            >
              <option value="all">كل المستويات</option>
              <option value="beginner">مبتدئ</option>
              <option value="intermediate">متوسط</option>
              <option value="advanced">متقدم</option>
            </select>

            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            >
              <option value="all">كل المصادر</option>
              <option value="whatsapp-scraper">سحب واتساب</option>
              <option value="csv">استيراد CSV</option>
              <option value="facebook-lead">حملة ممولة</option>
              <option value="signup">تسجيل من المنصة</option>
              <option value="manual">إضافة يدوية</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            >
              <option value="newest">الأحدث تسجيلاً</option>
              <option value="lastMessage">حسب وقت رسالة الواتساب</option>
            </select>

            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            />

            {(filterStatus !== 'all' || filterLevel !== 'all' || filterDate !== '' || filterSource !== 'all' || sortBy !== 'newest') && (
              <button
                onClick={() => { setFilterStatus('all'); setFilterLevel('all'); setFilterDate(''); setFilterSource('all'); setSortBy('newest'); }}
                className="text-mdfont-bold text-slate-400 hover:text-red-500 transition-colors mr-auto"
              >
                مسح الفلاتر
              </button>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
              <p className="font-bold">جاري تحميل البيانات...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <AlertCircle className="w-12 h-12 text-slate-300 dark:text-zinc-700 mb-4" />
              <p className="text-lg font-bold">لا يوجد مسجلين يطابقون بحثك</p>
            </div>
          ) : (
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-zinc-900 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 text-lg font-bold">
                  <th className="px-4 py-4 whitespace-nowrap w-10">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                      onChange={toggleAllUsers}
                      className="w-4 h-4 rounded text-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-4 whitespace-nowrap">الاسم</th>
                  <th className="px-6 py-4 whitespace-nowrap">البريد الإلكتروني</th>
                  <th className="px-6 py-4 whitespace-nowrap">رقم الواتساب</th>
                  <th className="px-6 py-4 whitespace-nowrap">المستوى</th>
                  <th className="px-6 py-4 whitespace-nowrap">التصنيف</th>
                  <th className="px-6 py-4 whitespace-nowrap">المبلغ ($)</th>
                  <th className="px-6 py-4 whitespace-nowrap">المصدر</th>
                  <th className="px-6 py-4 whitespace-nowrap">تاريخ التسجيل</th>
                  <th className="px-6 py-4 whitespace-nowrap">آخر محادثة</th>
                  <th className="px-6 py-4 min-w-[150px]">الملاحظات</th>
                  <th className="px-6 py-4 whitespace-nowrap text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={`hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors group ${selectedUsers.includes(user.id) ? 'bg-blue-50/50 dark:bg-blue-500/5' : ''}`}>
                    <td className="px-4 py-5" onClick={() => toggleUser(user.id)}>
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => { }}
                        className="w-4 h-4 rounded text-blue-500 cursor-pointer pointer-events-none"
                      />
                    </td>
                    <td className="px-6 py-5 font-bold text-slate-900 dark:text-white whitespace-nowrap cursor-pointer" onClick={() => toggleUser(user.id)}>
                      {user.name}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      {user.email ? (
                        <a href={`mailto:${user.email}`} className="text-slate-600 dark:text-zinc-300 hover:text-blue-600 font-medium font-sans" dir="ltr">
                          {user.email}
                        </a>
                      ) : (
                        <span className="text-slate-400 dark:text-zinc-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <a
                        href={`https://wa.me/${user.whatsapp.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-slate-600 dark:text-zinc-300 hover:text-green-600 font-medium font-sans"
                        dir="ltr"
                      >
                        +{user.whatsapp.replace(/\D/g, '')}
                      </a>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-md font-bold ${user.level === 'beginner'
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400'
                        : 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400'
                        }`}>
                        {user.level === 'beginner' ? 'مبتدئ' : user.level === 'intermediate' ? 'متوسط' : user.level === 'advanced' ? 'متقدم' : user.level}
                      </span>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      <select
                        value={user.status || 'interested'}
                        onChange={(e) => handleStatusChange(user.id, e.target.value)}
                        className={`text-md font-bold rounded-full px-3 py-1 border outline-none transition-colors cursor-pointer appearance-none ${(!user.status || user.status === 'interested')
                          ? 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20'
                          : user.status === 'contacted'
                            ? 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20'
                            : 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20'
                          }`}
                        style={{ textAlignLast: 'center' }}
                      >
                        <option value="interested" className="text-slate-900 bg-white font-sans font-bold">مهتم</option>
                        <option value="contacted" className="text-slate-900 bg-white font-sans font-bold">تم التواصل معه</option>
                        <option value="customer" className="text-slate-900 bg-white font-sans font-bold">عميل</option>
                      </select>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      {user.status === 'customer' ? (
                        <div className="relative w-24">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                          <input
                            type="number"
                            defaultValue={user.amountPaid || 0}
                            onBlur={(e) => handleAmountChange(user.id, e.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-lg outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all font-bold text-slate-900 dark:text-white"
                            min="0"
                            dir="ltr"
                          />
                        </div>
                      ) : (
                        <span className="text-slate-400 dark:text-zinc-500 font-bold">-</span>
                      )}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap">
                      {user.source === 'whatsapp-scraper' ? (
                        <span className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 px-3 py-1 rounded-lg text-md font-bold whitespace-nowrap border border-emerald-200 dark:border-emerald-500/30">سحب من واتساب</span>
                      ) : user.source === 'csv' ? (
                        <span className="bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 px-3 py-1 rounded-lg text-md font-bold whitespace-nowrap border border-blue-200 dark:border-blue-500/30">استيراد CSV</span>
                      ) : user.source === 'facebook-lead' || user.source === 'meta-ads' ? (
                        <span className="bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 px-3 py-1 rounded-lg text-md font-bold whitespace-nowrap border border-indigo-200 dark:border-indigo-500/30">حملة ممولة</span>
                      ) : user.source === 'signup' ? (
                        <span className="bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-400 px-3 py-1 rounded-lg text-md font-bold whitespace-nowrap border border-violet-200 dark:border-violet-500/30">تسجيل من المنصة</span>
                      ) : (
                        <span className="bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-zinc-300 px-3 py-1 rounded-lg text-md font-bold whitespace-nowrap border border-slate-200 dark:border-white/20">إضافة يدوية</span>
                      )}
                    </td>
                    <td className="px-6 py-5 text-lg text-slate-500 dark:text-zinc-400 font-medium whitespace-nowrap">
                      {user.dateStr}
                    </td>
                    <td className="px-6 py-5 text-slate-500 dark:text-zinc-400 font-medium whitespace-nowrap text-md" dir="ltr">
                      {user.lastChatDate ? new Date(user.lastChatDate).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                    </td>
                    <td className="px-6 py-5">
                      <div
                        onClick={() => {
                          setCurrentNoteUser(user);
                          setNoteText(user.notes || '');
                          setIsNotesModalOpen(true);
                        }}
                        className={`cursor-pointer max-w-[150px] truncate ${user.notes ? 'text-slate-700 dark:text-zinc-300 font-bold' : 'text-slate-400 dark:text-zinc-500 text-mditalic'}`}
                        title="انقر لتعديل الملاحظات"
                      >
                        {user.notes || 'أضف ملاحظة...'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                        <a
                          href={`https://wa.me/${user.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-2 text-slate-400 hover:text-emerald-600 bg-slate-100 hover:bg-emerald-50 dark:bg-white/5 dark:hover:bg-emerald-500/10 rounded-lg transition-colors"
                          title="مراسلة واتساب"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDelete(user.id)}
                          disabled={isDeleting === user.id}
                          className="p-2 text-slate-400 hover:text-red-600 bg-slate-100 hover:bg-red-50 dark:bg-white/5 dark:hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                          title="حذف السجل"
                        >
                          {isDeleting === user.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Add Client Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-white/10 flex justify-between items-center bg-slate-50 dark:bg-zinc-800/50">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">إضافة عميل جديد</h2>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddClient} className="p-6 space-y-4">
              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">الاسم الكامل <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  placeholder="محمد أحمد"
                />
              </div>

              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={newClient.email || ''}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  placeholder="example@gmail.com"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>

              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">رقم الواتساب <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={newClient.whatsapp}
                  onChange={(e) => setNewClient({ ...newClient, whatsapp: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  placeholder="201xxxxxxxxx"
                  dir="ltr"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">المستوى</label>
                  <select
                    value={newClient.level}
                    onChange={(e) => setNewClient({ ...newClient, level: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  >
                    <option value="beginner">مبتدئ</option>
                    <option value="intermediate">متوسط</option>
                    <option value="advanced">متقدم</option>
                  </select>
                </div>

                <div>
                  <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">المبلغ المدفوع ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={newClient.amountPaid}
                    onChange={(e) => setNewClient({ ...newClient, amountPaid: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="submit"
                  disabled={isAdding}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl font-bold transition-all disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {isAdding ? <Loader2 className="w-5 h-5 animate-spin" /> : 'إضافة العميل'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-900 dark:text-white py-3 rounded-xl font-bold transition-all"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {isNotesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-white/10 flex justify-between items-center bg-slate-50 dark:bg-zinc-800/50">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">ملاحظات: {currentNoteUser?.name}</h2>
              <button
                onClick={() => setIsNotesModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-2">اكتب ملاحظاتك هنا</label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows="5"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-medium text-slate-900 dark:text-white resize-none"
                  placeholder="أضف أي تفاصيل أو ملاحظات حول مستوى العميل، طلباته، إلخ..."
                ></textarea>
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  onClick={handleSaveNote}
                  disabled={isSavingNote}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl font-bold transition-all disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {isSavingNote ? <Loader2 className="w-5 h-5 animate-spin" /> : 'حفظ الملاحظات'}
                </button>
                <button
                  onClick={() => setIsNotesModalOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-900 dark:text-white py-3 rounded-xl font-bold transition-all"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
